import { GripVertical } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Header } from '@tanstack/react-table'

interface ColumnResizeHandleProps<TData> {
  header: Header<TData, unknown>
  columnName?: string
}

/**
 * Column Resize Handle Component
 *
 * Provides a draggable grip icon for resizing table columns.
 * Includes full accessibility support for keyboard users.
 *
 * Accessibility features:
 * - ARIA label for screen readers
 * - Keyboard support (Arrow keys to resize)
 * - Focus indicators
 * - Touch-friendly sizing (44x44px minimum)
 */
export function ColumnResizeHandle<TData>({ header, columnName }: ColumnResizeHandleProps<TData>) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const column = header.column
    const currentSize = column.getSize()
    let newSize: number | null = null

    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      newSize = Math.max(column.columnDef.minSize ?? 50, currentSize - 10)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      newSize = Math.min(column.columnDef.maxSize ?? 1000, currentSize + 10)
    }

    if (newSize !== null) {
      header.getContext().table.setColumnSizing((prev: Record<string, number>) => ({
        ...prev,
        [column.id]: newSize,
      }))
      announceWidthChange(columnName || column.id, newSize)
    }
  }

  const announceWidthChange = (colName: string, width: number) => {
    // Create temporary element for screen reader announcement
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'status')
    announcement.setAttribute('aria-live', 'polite')
    announcement.className = 'sr-only'
    announcement.textContent = `${colName} column width changed to ${width} pixels`
    document.body.appendChild(announcement)
    setTimeout(() => document.body.removeChild(announcement), 1000)
  }

  return (
    <div
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${columnName || header.column.id} column`}
      className={cn(
        'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
        'flex items-center justify-center',
        'hover:bg-primary/20',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'group',
        // Min touch target size for accessibility (WCAG 2.2)
        'min-w-[44px]'
      )}
      style={{
        // Keep visual resize indicator in position
        transform: header.column.getIsResizing() ? 'translateX(0)' : undefined,
      }}
    >
      <div
        className={cn(
          'flex items-center justify-center w-4 h-8 rounded',
          'text-muted-foreground/40',
          'group-hover:text-muted-foreground',
          'group-focus-visible:text-primary',
          'transition-colors'
        )}
      >
        <GripVertical className="h-4 w-4" aria-hidden="true" />
      </div>

      {/* Visual indicator when actively resizing */}
      {header.column.getIsResizing() && (
        <div className="absolute inset-y-0 right-0 w-0.5 bg-primary" />
      )}
    </div>
  )
}
